import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "react-day-picker/locale";
import { ArrowDownRight, ArrowUpRight, CalendarIcon, Minus } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatAmountMinor,
  MAX_CUSTOM_RANGE_DAYS,
  platformMetricsService,
  rangeFromPreset,
  type CountDelta,
  type MetricsRangeParams,
  type RangePreset,
} from "@/services/platformMetricsService";

type Preset = RangePreset | "custom";

const PRESET_LABELS: Record<Preset, string> = {
  "24h": "Hoy (últimas 24 h)",
  "7d": "Últimos 7 días",
  "30d": "Últimos 30 días",
  custom: "Personalizado",
};

const REQUEST_TYPE_LABELS: Record<string, string> = {
  income_certificate: "Certificado de ingresos",
  notarial_legal_entity: "Notarial persona jurídica",
};

function defaultCustomRange(): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { from, to };
}

function rangeParamsFromDates(from: Date, to: Date): MetricsRangeParams {
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatRangeLabel(from: Date, to: Date): string {
  return `${format(from, "dd/MM/yyyy", { locale: es })} → ${format(to, "dd/MM/yyyy", { locale: es })}`;
}

export function PlatformAnalyticsPage(): JSX.Element {
  const [preset, setPreset] = useState<Preset>("7d");
  const [customRange, setCustomRange] = useState<{ from: Date; to: Date }>(
    defaultCustomRange,
  );

  const range = useMemo<MetricsRangeParams>(() => {
    if (preset === "custom") {
      return rangeParamsFromDates(customRange.from, customRange.to);
    }
    return rangeFromPreset(preset);
  }, [preset, customRange]);

  const summary = useQuery({
    queryKey: ["platform-metrics", "summary", range],
    queryFn: () => platformMetricsService.summary(range),
  });

  const invoicesByTenant = useQuery({
    queryKey: ["platform-metrics", "invoices-by-tenant", range],
    queryFn: () => platformMetricsService.invoicesByTenant(range),
  });

  const requestsByType = useQuery({
    queryKey: ["platform-metrics", "requests-by-type", range],
    queryFn: () => platformMetricsService.requestsByType(range),
  });

  return (
    <div className="space-y-6 p-4 md:p-8">
      <PageHeader
        title="Métricas de la plataforma"
        description="Actividad operativa por período: usuarios, solicitudes, facturas y cobro."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rango:</span>
          <Select value={preset} onValueChange={(v) => setPreset(v as Preset)}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">{PRESET_LABELS["24h"]}</SelectItem>
              <SelectItem value="7d">{PRESET_LABELS["7d"]}</SelectItem>
              <SelectItem value="30d">{PRESET_LABELS["30d"]}</SelectItem>
              <SelectItem value="custom">{PRESET_LABELS.custom}</SelectItem>
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <CustomRangePicker value={customRange} onChange={setCustomRange} />
          )}
        </div>
      </div>

      <SummaryGrid
        loading={summary.isLoading}
        data={summary.data}
        rangeLabel={PRESET_LABELS[preset]}
      />

      <Card>
        <CardHeader>
          <CardTitle>Facturas por tenant</CardTitle>
          <p className="text-sm text-muted-foreground">
            Solicitudes empresariales agrupadas por tenant en el período (top 50,
            ordenadas por volumen).
          </p>
        </CardHeader>
        <CardContent>
          <InvoicesByTenantTable
            loading={invoicesByTenant.isLoading}
            rows={invoicesByTenant.data ?? []}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Solicitudes por tipo de gestión</CardTitle>
          <p className="text-sm text-muted-foreground">
            Distribución de solicitudes consumer (notarial + contador) por tipo
            de trámite en el período.
          </p>
        </CardHeader>
        <CardContent>
          <RequestsByTypeTable
            loading={requestsByType.isLoading}
            rows={requestsByType.data ?? []}
          />
        </CardContent>
      </Card>
    </div>
  );
}

type SummaryGridProps = {
  loading: boolean;
  data: import("@/services/platformMetricsService").PlatformMetricsSummary | undefined;
  rangeLabel: string;
};

function SummaryGrid({ loading, data, rangeLabel }: SummaryGridProps): JSX.Element {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
        label="Nuevos usuarios"
        value={data.newUsers.total.current.toLocaleString("es-UY")}
        delta={data.newUsers.total}
        rangeLabel={rangeLabel}
        sub={`${data.newUsers.consumers.current} consumidores · ${data.newUsers.accountants.current} contadores · ${data.newUsers.notaries.current} escribanos`}
      />
      <StatCard
        label="Solicitudes creadas"
        value={data.requestsCreated.total.current.toLocaleString("es-UY")}
        delta={data.requestsCreated.total}
        rangeLabel={rangeLabel}
        sub={`${data.requestsCreated.notarial.current} notarial · ${data.requestsCreated.accountant.current} contador · ${data.requestsCreated.enterprise.current} enterprise`}
      />
      <StatCard
        label="Facturas generadas"
        value={data.invoicesGenerated.count.current.toLocaleString("es-UY")}
        delta={data.invoicesGenerated.count}
        rangeLabel={rangeLabel}
        sub="Solicitudes con factura Fiscalo emitida"
      />
      <StatCard
        label={`Cobrado (${data.paidCheckouts.totalAmount.currency})`}
        value={formatAmountMinor(
          data.paidCheckouts.totalAmount.currentMinor,
          data.paidCheckouts.totalAmount.currency,
        )}
        delta={amountDelta(data.paidCheckouts.totalAmount)}
        rangeLabel={rangeLabel}
        sub={`${data.paidCheckouts.count.current} checkouts pagos`}
      />
      {/* Beside GMV, never instead of it: "cobrado" is what passed through the
          platform and "ingresos" is what stayed. Reading the first as the
          second is what this whole change exists to stop. */}
      <StatCard
        label={`Ingresos Gestionalo (${data.paidCheckouts.platformRevenue.currency})`}
        value={formatAmountMinor(
          data.paidCheckouts.platformRevenue.currentMinor,
          data.paidCheckouts.platformRevenue.currency,
        )}
        delta={amountDelta(data.paidCheckouts.platformRevenue)}
        rangeLabel={rangeLabel}
        sub={
          data.paidCheckouts.checkoutsWithoutRecordedFee.current > 0
            ? `Cargo por servicio + comisión · ${data.paidCheckouts.checkoutsWithoutRecordedFee.current} checkout(s) sin cargo registrado: el total está subestimado`
            : "Cargo por servicio + comisión"
        }
      />
      <StatCard
        label="Tenants activos"
        value={data.activeTenants.current.toLocaleString("es-UY")}
        delta={data.activeTenants}
        rangeLabel={rangeLabel}
        sub="Empresas con al menos una solicitud en el período"
      />
      <StatCard
        label="Conversión"
        value={formatPercent(data.conversionRate.current)}
        delta={percentDelta(data.conversionRate)}
        rangeLabel={rangeLabel}
        sub="Checkouts pagos / solicitudes creadas"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  delta,
  rangeLabel,
  sub,
}: {
  label: string;
  value: string;
  delta: CountDelta;
  rangeLabel: string;
  sub?: string;
}): JSX.Element {
  const deltaInfo = formatDelta(delta);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-3xl font-semibold leading-none tracking-tight">
          {value}
        </div>
        <div
          className={`inline-flex items-center gap-1 text-xs font-medium ${deltaInfo.colorClass}`}
        >
          {deltaInfo.icon}
          <span>{deltaInfo.label}</span>
          <span className="text-muted-foreground">· vs período anterior</span>
        </div>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        <p className="sr-only">Período actual: {rangeLabel}</p>
      </CardContent>
    </Card>
  );
}

function formatDelta(delta: CountDelta): {
  label: string;
  colorClass: string;
  icon: JSX.Element;
} {
  const change = delta.current - delta.previous;
  if (delta.previous === 0 && delta.current === 0) {
    return {
      label: "Sin cambio",
      colorClass: "text-muted-foreground",
      icon: <Minus className="h-3 w-3" aria-hidden />,
    };
  }
  if (delta.previous === 0) {
    return {
      label: `+${delta.current}`,
      colorClass: "text-emerald-600",
      icon: <ArrowUpRight className="h-3 w-3" aria-hidden />,
    };
  }
  const sign = change > 0 ? "+" : "";
  const pct = (change / delta.previous) * 100;
  return {
    label: `${sign}${change.toLocaleString("es-UY")} (${sign}${pct.toFixed(1)}%)`,
    colorClass:
      change > 0
        ? "text-emerald-600"
        : change < 0
          ? "text-rose-600"
          : "text-muted-foreground",
    icon:
      change > 0 ? (
        <ArrowUpRight className="h-3 w-3" aria-hidden />
      ) : change < 0 ? (
        <ArrowDownRight className="h-3 w-3" aria-hidden />
      ) : (
        <Minus className="h-3 w-3" aria-hidden />
      ),
  };
}

function amountDelta(amount: {
  currentMinor: number;
  previousMinor: number;
}): CountDelta {
  return { current: amount.currentMinor, previous: amount.previousMinor };
}

function percentDelta(rate: { current: number; previous: number }): CountDelta {
  // Render the conversion-rate stat's delta as basis points so the icon /
  // color helper can stay the same shape as count deltas.
  return {
    current: Math.round(rate.current * 10000),
    previous: Math.round(rate.previous * 10000),
  };
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function InvoicesByTenantTable({
  loading,
  rows,
}: {
  loading: boolean;
  rows: import("@/services/platformMetricsService").InvoicesByTenantRow[];
}): JSX.Element {
  if (loading) {
    return <div className="h-32 animate-pulse rounded bg-muted" />;
  }
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Sin solicitudes empresariales en el período.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead className="text-right">Solicitudes</TableHead>
          <TableHead className="text-right">Completadas</TableHead>
          <TableHead className="text-right">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.tenantId}>
            <TableCell className="font-medium">{row.tenantName}</TableCell>
            <TableCell className="text-right">
              {row.requestCount.toLocaleString("es-UY")}
            </TableCell>
            <TableCell className="text-right">
              {row.completedCount.toLocaleString("es-UY")}
            </TableCell>
            <TableCell className="text-right font-mono text-xs">
              {row.totalAmountMinor > 0
                ? formatAmountMinor(row.totalAmountMinor, row.currency)
                : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RequestsByTypeTable({
  loading,
  rows,
}: {
  loading: boolean;
  rows: import("@/services/platformMetricsService").RequestsByTypeRow[];
}): JSX.Element {
  if (loading) {
    return <div className="h-32 animate-pulse rounded bg-muted" />;
  }
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Sin solicitudes en el período.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo de gestión</TableHead>
          <TableHead>Profesional</TableHead>
          <TableHead className="text-right">Solicitudes</TableHead>
          <TableHead className="text-right">Completadas</TableHead>
          <TableHead className="text-right">% completitud</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const pct = row.count > 0 ? (row.completedCount / row.count) * 100 : 0;
          return (
            <TableRow key={`${row.source}:${row.targetType}`}>
              <TableCell className="font-medium">
                {REQUEST_TYPE_LABELS[row.targetType] ?? row.targetType}
              </TableCell>
              <TableCell className="text-xs uppercase tracking-wide text-muted-foreground">
                {row.source === "notarial" ? "Escribano" : "Contador"}
              </TableCell>
              <TableCell className="text-right">
                {row.count.toLocaleString("es-UY")}
              </TableCell>
              <TableCell className="text-right">
                {row.completedCount.toLocaleString("es-UY")}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {pct.toFixed(0)}%
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function CustomRangePicker({
  value,
  onChange,
}: {
  value: { from: Date; to: Date };
  onChange: (next: { from: Date; to: Date }) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>({
    from: value.from,
    to: value.to,
  });

  const apply = (): void => {
    if (draft?.from && draft?.to) {
      const daysSpan =
        (draft.to.getTime() - draft.from.getTime()) / (24 * 60 * 60 * 1000);
      if (daysSpan > MAX_CUSTOM_RANGE_DAYS) return;
      onChange({ from: draft.from, to: draft.to });
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <CalendarIcon className="h-4 w-4" />
          {formatRangeLabel(value.from, value.to)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          locale={es}
          selected={draft}
          onSelect={setDraft}
          numberOfMonths={2}
        />
        <div className="flex items-center justify-end gap-2 border-t p-3">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={apply}
            disabled={!draft?.from || !draft?.to}
          >
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
